import type { SetRequiredDeep } from 'type-fest'
import * as zod from 'zod'
import { COMMON_WIDGET_DATA_CONSTRUCTOR, COMMON_WIDGET_ELEMENT_DATA_CONSTRUCTOR, PAGE, WidgetElementSchema, WidgetSchema, type AbstractWidgetData, type BaseWidgetElementDialogProps, type PartialWidget, type PartialWidgetElement, type WidgetConstructor, type WidgetElementConstructor, type WidgetElementData } from './Common'
import type { BaseWidgetDataRequest, WidgetDataResponse } from './Widgets'

export type LineVisibilitySettings = {
  name: string
  settings: PageWidgetLineSettings
}

export type PageWidgetOptionsResponse = WidgetDataResponse & {
  type: 'PAGE'
  lines: PageWidgetElementData[]
  linesSettings: LineVisibilitySettings[]
}

export type PageWidgetDataRequest = BaseWidgetDataRequest & {
  type: 'PAGE'
  widget: PageWidget
}
export const PageWidgetElementSchema = WidgetElementSchema.omit({ lineNumber: true }).extend({
  type: PAGE,
})
// .omit({ lineNumber: true }) // because backend always returns a null lineNumber
export type PageWidgetElement = zod.infer<typeof PageWidgetElementSchema>
export type PageWidgetElementPartial = SetRequiredDeep<PartialWidgetElement<PageWidgetElement>, 'type'>

const PageWidgetLineSettingsSchema = zod.object({
  lineNumber: zod.number(),
  visible: zod.boolean().default(true),
})
export type PageWidgetLineSettings = zod.infer<typeof PageWidgetLineSettingsSchema>

export const PageWidgetSchema = WidgetSchema.extend({
  type: PAGE,
  elements: PageWidgetElementSchema.array(),
  linesSettings: PageWidgetLineSettingsSchema.array(),
})
export type PageWidget = zod.infer<typeof PageWidgetSchema>
export type PageWidgetPartial = SetRequiredDeep<PartialWidget<PageWidget>, 'type' | 'elements' | `elements.${number}.type`>

export interface PageWidgetElementData extends WidgetElementData {
  readOnly: boolean
}
export interface PageWidgetData extends AbstractWidgetData<PageWidgetElementData> {
  type: 'PAGE'
  elements: PageWidgetElementData[]
}
export interface PageWidgetElementDialogProps extends BaseWidgetElementDialogProps {
  type: 'PAGE'
}

export const DEFAULT_PAGE_WIDGET_ELEMENT_CONSTRUCTOR: WidgetElementConstructor<PageWidgetElementPartial> = () => ({
  ...COMMON_WIDGET_ELEMENT_DATA_CONSTRUCTOR(),
  type: 'PAGE',
})

export const PAGE_WIDGET_CONSTRUCTOR: WidgetConstructor<PageWidgetPartial> = () => ({
  type: 'PAGE',
  ...COMMON_WIDGET_DATA_CONSTRUCTOR(),
  linesSettings: [],
  elements: [DEFAULT_PAGE_WIDGET_ELEMENT_CONSTRUCTOR()],
})
